import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, FileText, Truck, CalendarClock, Package,
  Printer, Download, ShieldCheck, ChevronRight, Layers,
  Mailbox, Layers3,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import AppFooter from '../components/AppFooter';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCS = [
  {
    id: 'audit-sheet',
    Icon: FileText,
    title: 'Master Account Audit Sheet',
    tag: '5-account ledger',
    blurb:
      'The single sheet where you list the (up to five) accounts you are disputing this round — creditor, last-four, bureau, balance, reason filed, date mailed, response due, status, notes.',
    citation: 'FCRA §611(a)(3)(A) — max 5 items per round',
  },
  {
    id: 'chain-of-custody',
    Icon: Truck,
    title: 'Chain of Custody Table',
    tag: 'Certified mail evidence',
    blurb:
      'One row per certified letter. Records the USPS tracking number, mail date, delivery confirmation, response received date and type — the paper trail if you ever need to escalate to CFPB or a court.',
    citation: '15 U.S.C. §1681i(a) · Retain with return receipt cards',
  },
  {
    id: 'compliance-calendar',
    Icon: CalendarClock,
    title: 'Compliance Calendar',
    tag: '30-day window tracker',
    blurb:
      'A printable timeline for every letter that shows Day 0 (mailed), Day 15 (delivery checkpoint), Day 30 (statutory response due), Day 35 (mail buffer), and Day 45 (extended window if you submitted more info).',
    citation: 'FCRA §611(a)(1) 30-day statutory investigation window',
  },
];

const fmtDue = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch {
    return null;
  }
};

const downloadDoc = async (id, prefill) => {
  const label = DOCS.find((d) => d.id === id)?.title || id;
  try {
    const res = await axios.get(
      `${API_URL}/api/case-files/${id}.pdf?prefill=${prefill}`,
      { responseType: 'blob' },
    );
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selah-slate-${id}-${prefill ? 'case' : 'blank'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${label} downloaded.`);
  } catch (err) {
    toast.error(err?.response?.status === 401
      ? 'Please sign in again to download case files.'
      : `Could not download ${label}. Please try again.`);
  }
};

const downloadPacket = async (prefill) => {
  try {
    const res = await axios.get(
      `${API_URL}/api/case-files/packet.pdf?prefill=${prefill}`,
      { responseType: 'blob' },
    );
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selah-slate-case-file-packet-${prefill ? 'case' : 'blank'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Case file packet downloaded — 3 sheets, one PDF.');
  } catch {
    toast.error('Could not download the packet. Please try again.');
  }
};

export default function CaseFilesPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          axios.get(`${API_URL}/api/case-files/summary`),
          axios.get(`${API_URL}/api/disputes/channels`),
        ]);
        setSummary(s.data);
        setChannels(c.data);
      } catch { /* silent — page still works with blanks */ }
      finally { setLoading(false); }
    })();
  }, []);

  const canPrefill = !!summary?.can_prefill;
  const nextDue = fmtDue(summary?.next_response_due);

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="case-files-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center text-[#36454F]/60 hover:text-[#B76E79] text-sm font-['Manrope'] mb-6 transition-colors"
          data-testid="case-files-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
          Back to dashboard
        </Link>

        <div className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#B76E79] font-['Manrope'] font-semibold mb-2">
            Print Station &amp; Case Files
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-semibold text-[#36454F] mb-3">
            Your factual tracking ledger.
          </h1>
          <p className="text-[#36454F]/70 font-['Manrope'] text-base leading-relaxed max-w-2xl">
            Selah Slate does the digital work. This page prints the paper. Download the three sheets
            you need to audit your five designated accounts, log every certified-mail tracking number,
            and hold each bureau to the FCRA 30-day statutory response window.
          </p>
        </div>

        {/* Case metadata banner */}
        <Card
          className="mb-8 border-[#B76E79]/25 bg-gradient-to-br from-[#B76E79]/8 to-white"
          data-testid="case-summary-card"
        >
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-sm bg-[#B76E79]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <ShieldCheck className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-['Manrope'] font-semibold">
                  Case identifier
                </p>
                <p
                  className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mt-0.5"
                  data-testid="case-id"
                >
                  {loading ? 'Loading…' : (summary?.case_id || 'SS-PREVIEW')}
                </p>
                <p className="text-xs text-[#36454F]/60 font-['Manrope'] mt-1">
                  {canPrefill
                    ? `Auto-fill ready: ${summary.rows_available} disputed account row${summary.rows_available === 1 ? '' : 's'} · ${summary.letters_available} certified letter${summary.letters_available === 1 ? '' : 's'}.`
                    : 'No active dispute yet — download the blank sheets and file them by hand as you build your round.'}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-row gap-3 flex-shrink-0">
              {summary?.round_number != null && (
                <div className="border border-[#B76E79]/25 bg-white/70 rounded-sm px-4 py-2 text-right min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-['Manrope'] font-semibold">
                    FCRA Round
                  </p>
                  <p
                    className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mt-0.5"
                    data-testid="round-number"
                  >
                    Round {summary.round_number}
                  </p>
                </div>
              )}
              {nextDue && (
                <div className="border border-[#B76E79]/25 bg-white/70 rounded-sm px-4 py-2 text-right">
                  <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-['Manrope'] font-semibold">
                    Next response due
                  </p>
                  <p
                    className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mt-0.5"
                    data-testid="next-response-due"
                  >
                    {nextDue}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Three individual document cards */}
        <div className="space-y-4">
          {DOCS.map(({ id, Icon, title, tag, blurb, citation }) => (
            <Card
              key={id}
              className="border-[#36454F]/12 bg-white hover:border-[#B76E79]/40 transition-colors"
              data-testid={`case-file-card-${id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-sm bg-[#F5EBE8] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                          {title}
                        </h3>
                        <span className="text-[10px] uppercase tracking-wider bg-[#36454F]/8 text-[#36454F]/70 px-2 py-0.5 rounded font-['Manrope'] font-medium">
                          {tag}
                        </span>
                      </div>
                      <p className="text-sm text-[#36454F]/70 font-['Manrope'] leading-relaxed max-w-2xl">
                        {blurb}
                      </p>
                      <p className="text-[11px] text-[#36454F]/40 font-['Manrope'] italic mt-2">
                        {citation}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:min-w-[180px] flex-shrink-0">
                    <Button
                      onClick={() => downloadDoc(id, true)}
                      disabled={loading}
                      className="h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-sm w-full"
                      data-testid={`download-${id}-case`}
                    >
                      <Printer className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                      {canPrefill ? 'Download with my case' : 'Download (empty case)'}
                    </Button>
                    <Button
                      onClick={() => downloadDoc(id, false)}
                      variant="outline"
                      className="h-9 border-[#36454F]/20 text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm text-sm w-full"
                      data-testid={`download-${id}-blank`}
                    >
                      <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                      Blank
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Multi-Round Dispute Tracker — "which channel started each dispute?" */}
        {channels && channels.total_disputes > 0 && (
          <Card
            className="mt-8 border-[#36454F]/15 bg-white"
            data-testid="dispute-tracker-card"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-['Manrope'] font-semibold">
                    Multi-round dispute tracker
                  </p>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mt-0.5">
                    Where every letter started
                  </h3>
                  <p className="text-sm text-[#36454F]/60 font-['Manrope'] mt-1 max-w-2xl">
                    {channels.total_disputes} dispute letter{channels.total_disputes === 1 ? '' : 's'} drafted across {channels.round_count} FCRA round{channels.round_count === 1 ? '' : 's'} — grouped by which Selah Slate flow kicked each one off.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {channels.channels.map((c) => {
                  const Icon = c.icon === 'layers' ? Layers : c.icon === 'layers-2' ? Layers3 : c.icon === 'mailbox' ? Mailbox : FileText;
                  const pct = Math.round((c.count / channels.total_disputes) * 100);
                  return (
                    <div
                      key={c.channel}
                      className="p-4 border border-[#36454F]/10 rounded-sm bg-[#F5F5F5]"
                      data-testid={`channel-row-${c.channel}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-sm bg-[#B76E79]/12 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-[#B76E79]" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-['Manrope'] font-semibold text-[#36454F] text-sm truncate">{c.label}</p>
                          <p className="text-[11px] text-[#36454F]/50 font-['Manrope']">
                            {c.count} letter{c.count === 1 ? '' : 's'} · {c.mailed} mailed · {pct}% of total
                          </p>
                        </div>
                      </div>
                      <div className="w-full bg-white border border-[#36454F]/10 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-[#B76E79] h-full rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-[11px] text-[#36454F]/55 font-['Manrope'] mt-2 leading-relaxed">{c.description}</p>
                    </div>
                  );
                })}
              </div>

              {/* Round-by-round mini-timeline */}
              {channels.rounds && channels.rounds.length > 0 && (
                <div className="border-t border-[#36454F]/10 pt-5" data-testid="rounds-timeline">
                  <p className="text-[10px] uppercase tracking-widest text-[#36454F]/60 font-['Manrope'] font-semibold mb-3">
                    Round-by-round history
                  </p>
                  <div className="flex items-end gap-2 overflow-x-auto pb-1">
                    {channels.rounds.map((r, idx) => {
                      const maxCount = Math.max(...channels.rounds.map((rr) => rr.count));
                      const h = Math.max(16, Math.round((r.count / maxCount) * 56));
                      return (
                        <div key={r.round_key} className="flex-shrink-0 flex flex-col items-center" title={`${r.count} letter${r.count === 1 ? '' : 's'} · ${r.bureaus.join(', ')}`} data-testid={`round-bar-${idx}`}>
                          <div className="text-[10px] text-[#36454F]/55 font-['Manrope'] mb-1">{r.count}</div>
                          <div
                            className="w-6 bg-[#B76E79] rounded-t"
                            style={{ height: `${h}px` }}
                          />
                          <div className="text-[9px] text-[#36454F]/50 font-['Manrope'] mt-1 font-mono">{r.round_key.replace('-W', '·W')}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* All-in-one packet */}
        <Card
          className="mt-8 border-[#36454F]/15 bg-[#36454F] text-white"
          data-testid="case-file-packet-card"
        >
          <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-sm bg-[#B76E79]/25 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-[#E0BFB8]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#E0BFB8] font-['Manrope'] font-semibold">
                  Cover + three sheets · stapled
                </p>
                <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-white mt-0.5">
                  Case File Packet
                </h3>
                <p className="text-sm text-white/70 font-['Manrope'] leading-relaxed mt-1 max-w-2xl">
                  Signed cover sheet (case ID, member name, round number) plus audit sheet, chain of
                  custody, and compliance calendar — one PDF, four pages, ready to slip into a
                  folder or a courtroom binder.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[180px] flex-shrink-0">
              <Button
                onClick={() => downloadPacket(true)}
                disabled={loading}
                className="h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-sm w-full"
                data-testid="download-packet-case"
              >
                <Printer className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Download packet
              </Button>
              <Button
                onClick={() => downloadPacket(false)}
                variant="outline"
                className="h-9 bg-transparent border-white/25 text-white hover:bg-white/10 font-['Manrope'] rounded-sm text-sm w-full"
                data-testid="download-packet-blank"
              >
                <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Blank packet
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Legal / privacy footer strip */}
        <div className="mt-8 border-l-4 border-[#B76E79] bg-white/60 p-5 rounded-sm">
          <p className="text-sm text-[#36454F]/75 font-['Manrope'] leading-relaxed">
            <strong className="text-[#36454F]">Educational tool, not legal advice.</strong>{' '}
            Selah Slate is not a credit-repair organization. These printable sheets are for your own
            recordkeeping under 15 U.S.C. §1681 et seq. The app analyzes your data — no one at
            Selah Slate ever reads it. Retain your ledger, USPS receipts, and any bureau
            correspondence together with this packet.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <Link
              to="/mail-hub"
              className="inline-flex items-center text-sm text-[#B76E79] hover:underline font-['Manrope']"
              data-testid="mail-hub-link"
            >
              Bureau addresses in Mail Hub
              <ChevronRight className="w-4 h-4 ml-0.5" strokeWidth={1.5} />
            </Link>
            <Link
              to="/disputes"
              className="inline-flex items-center text-sm text-[#36454F]/60 hover:text-[#36454F] font-['Manrope']"
              data-testid="disputes-link"
            >
              Live dispute tracker
              <ChevronRight className="w-4 h-4 ml-0.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>

      <AppFooter />
    </div>
  );
}
